import React from 'react';
import { deleteEvent, deleteEventItem, deleteEventPayment } from '../../utils/eventApi';
import { categoryEmoji, formatMoney } from '../../utils/eventUtils';
import EventDetailView from './EventDetailView';

// The Events tab of a group: the list of event cards, or one event opened in detail.
function GroupEventsTab({
  events,
  selectedEventId,
  onSelectEvent,
  currentUser,
  getName,
  canManageGroup,
  onOpenModal,
  onChanged,
}) {
  const selectedEvent = events.find((e) => e.id === selectedEventId);

  const runDelete = async (message, action) => {
    if (!window.confirm(message)) return false;
    try {
      await action();
      onChanged();
      return true;
    } catch (err) {
      alert(err.message || 'Something went wrong.');
      return false;
    }
  };

  if (selectedEvent) {
    const canManage = canManageGroup || selectedEvent.createdBy === currentUser.id;
    const canContribute = canManage || selectedEvent.memberIds.includes(currentUser.id);

    return (
      <EventDetailView
        event={selectedEvent}
        currentUser={currentUser}
        getName={getName}
        canManage={canManage}
        canContribute={canContribute}
        canEditItem={(item) => canManage || item.createdBy === currentUser.id}
        onBack={() => onSelectEvent(null)}
        onEditEvent={() => onOpenModal({ type: 'edit', eventId: selectedEvent.id })}
        onDeleteEvent={async () => {
          const deleted = await runDelete(
            `Are you sure you want to delete "${selectedEvent.name}" and all its payments? This action cannot be undone.`,
            () => deleteEvent(selectedEvent.id)
          );
          if (deleted) onSelectEvent(null);
        }}
        onAddItem={() => onOpenModal({ type: 'item', eventId: selectedEvent.id })}
        onEditItem={(item) => onOpenModal({ type: 'item', eventId: selectedEvent.id, item })}
        onChangeTotal={(item) => onOpenModal({ type: 'item', eventId: selectedEvent.id, item, changeTotal: true })}
        onDeleteItem={(item) =>
          runDelete(`Are you sure you want to delete "${item.name}"?`, () =>
            deleteEventItem(selectedEvent.id, item.id)
          )
        }
        onRecordPayment={(itemId) => onOpenModal({ type: 'payment', eventId: selectedEvent.id, itemId })}
        onDeletePayment={(payment) =>
          runDelete(`Are you sure you want to delete the payment "${payment.label}"?`, () =>
            deleteEventPayment(selectedEvent.id, payment.id)
          )
        }
      />
    );
  }

  return (
    <div className="card-box container-large">
      <div className="container-header">
        <h3>🎟️ Events</h3>
        <button className="btn btn-primary btn-sm" onClick={() => onOpenModal({ type: 'create' })}>
          ➕ New Event
        </button>
      </div>

      {events.length === 0 ? (
        <div className="empty-state">
          <p>
            No events yet. Create one for a shared bill that is paid in parts, like a hotel booking with an
            advance.
          </p>
        </div>
      ) : (
        <div className="event-card-list">
          {events.map((event) => {
            const { summary } = event;
            const paidPercent = Number(summary.total) > 0
              ? Math.min(100, Math.floor((Number(summary.paid) / Number(summary.total)) * 100))
              : 0;
            return (
              <button key={event.id} className="event-card" onClick={() => onSelectEvent(event.id)}>
                <div className="event-card-top">
                  <span className="event-card-name">
                    {categoryEmoji(event.category)} {event.name}
                  </span>
                  <span className="event-card-total">{formatMoney(summary.total)}</span>
                </div>
                <div className="event-progress">
                  <div className="event-progress-fill" style={{ width: `${paidPercent}%` }} />
                </div>
                <div className="event-card-meta">
                  <span>{formatMoney(summary.paid)} paid</span>
                  <span className={Number(summary.outstanding) > 0 ? 'negative' : 'positive'}>
                    {Number(summary.outstanding) > 0 ? `${formatMoney(summary.outstanding)} due` : 'Fully paid'}
                  </span>
                </div>
                {Number(summary.unsplit) > 0 && (
                  <div className="event-card-unsplit">{formatMoney(summary.unsplit)} not yet split</div>
                )}
                {summary.items.length > 0 && (
                  <div className="event-card-items">
                    {summary.items
                      .map((item) =>
                        Number(item.outstanding) > 0 && Number(summary.outstanding) > 0
                          ? `${item.name} ${formatMoney(item.total)} (${formatMoney(item.paid)} paid)`
                          : `${item.name} ${formatMoney(item.total)} (paid)`
                      )
                      .join(' · ')}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default GroupEventsTab;
